import * as React from 'react'
import { ReactElement, useMemo, useRef, useState } from 'react'

import OverflowToolbarPanel, { HammockToolbarButtonData, HammockToolbarProps } from '../../../../components/Overflow/OverflowToolbarPanel'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { usePageStyles, ViewFileProps } from '../ViewFile'
import DocumentFlatTree, { DocumentSelectionStrategy, FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import { Card, Input } from '@fluentui/react-components'
import {
    AddSquareMultipleFilled,
    AddSquareMultipleRegular,
    ArrowSortFilled,
    ArrowSortRegular,
    ArrowUploadFilled,
    ArrowUploadRegular,
    bundleIcon,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    CopyArrowRightFilled,
    CopyArrowRightRegular,
    CopySelectFilled,
    CopySelectRegular,
    DeleteFilled,
    DeleteRegular,
    DocumentAddFilled,
    DocumentAddRegular,
    DocumentPdfFilled,
    DocumentPdfRegular,
    FolderAddFilled,
    FolderAddRegular,
    MailAddFilled,
    MailAddRegular,
    MailAttachFilled,
    MailAttachRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons'
import FileDocumentTreeItem from './FileDocumentTreeItem'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import HammockDialog from '../../../components/Dialog/HammockDialog'
import { useTranslation } from 'react-i18next'
import CreateFolderDialogBody, {
    CreateFolderDialogBodyProps,
    CreateFolderDialogBodyValues,
    getCreateRootFolderDialogBodySchema,
    getCreateSubFolderDialogBodySchema,
} from '../../../components/Dialog/DriveItem/CreateFolderDialogBody'
import HammockFormDialog from '../../../components/Dialog/HammockFormDialog'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/OfficeDocumentDialogBody'
import { DocumentContext, DocumentSortOrder } from '../../../../config/documentConfig'
import PreviewDocumentDialogBody, { PreviewDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/PreviewDocumentDialogBody'
import MutateDocumentDialogBody, {
    getMutateDocumentDialogBodySchema,
    MutateDocumentAction,
    MutateDocumentDialogBodyProps,
    MutateDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/MutateDocumentDialogBody'
import DeleteDocumentDialogBody, { DeleteDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/DeleteDocumentDialogBody'
import { getDownloadUrl, getDriveItemDownloadUrl, isConvertableToPdf } from '../../../../helpers/documentHelper'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import {
    DocumentGenerationContextData,
    DocumentGenerationStatusData,
    DocumentGenerationType,
} from '../../../../hooks/document/useDocumentGenerationHandler'
import { QueueData, QueueStatus } from '../../../../hooks/useQueueStateHandler'
import { isOfficeInitialized } from '../../../../helpers/outlookHelper'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../../hooks/file/useFilePages'
import { ModuleType } from '../../../../context/GlobalContext'
import useModules from '../../../../hooks/module/useModules'
import { useHistory, useRouteMatch } from 'react-router-dom'
import ComposeEmailDialogBody, { ComposeEmailDialogBodyProps } from '../../../components/Dialog/OutlookItem/ComposeEmailDialogBody'
import { isAppContext } from '../../../../helpers/appHelper'
import { AppContext } from '../../../../config/appConfig'
import useAxios from '../../../../hooks/useAxios'
import { appInsightsReact } from '../../../../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'
import Dropzone from '../../../../components/Dropzone/Dropzone'
import ImportDocumentDialogBody, {
    getImportDocumentDialogBodySchema,
    ImportDocumentDialogBodyProps,
    ImportDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/ImportDocumentDialogBody'
import { DropzoneRef } from 'react-dropzone'

const AddIcon = bundleIcon(AddSquareMultipleFilled, AddSquareMultipleRegular)
const AttachIcon = bundleIcon(MailAttachFilled, MailAttachRegular)
const DocumentAddIcon = bundleIcon(DocumentAddFilled, DocumentAddRegular)
const TaskAddIcon = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)
const DocumentPdfIcon = bundleIcon(DocumentPdfFilled, DocumentPdfRegular)
const FolderAddIcon = bundleIcon(FolderAddFilled, FolderAddRegular)
const RenameIcon = bundleIcon(RenameFilled, RenameRegular)
const CopyIcon = bundleIcon(CopySelectFilled, CopySelectRegular)
const MoveIcon = bundleIcon(CopyArrowRightFilled, CopyArrowRightRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)
const ImportIcon = bundleIcon(ArrowUploadFilled, ArrowUploadRegular)
const SortIcon = bundleIcon(ArrowSortFilled, ArrowSortRegular)

type FileDocumentOverviewProps = ViewFileProps & {
    selectedDriveItems: FolderDriveItem[]
    setSelectedDriveItems: (items: FolderDriveItem[]) => void
}

export default function FileDocumentOverview(props: FileDocumentOverviewProps): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const modules = useModules()
    const pageStyles = usePageStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])
    const routeMatch = useRouteMatch<{ fileId: string }>()

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            size: 'small',
            className: pageStyles.toolbar,
        }),
        [smallMenu],
    )

    const [sortOrder, setSortOrder] = useState<DocumentSortOrder>(DocumentSortOrder.NameAsc)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [openCreateStandardDocument, setOpenCreateStandardDocument] = useState(false)
    const [openCreateRootFolder, setOpenCreateRootFolder] = useState(false)
    const [openCreateSubFolder, setOpenCreateSubFolder] = useState(false)
    const [openMutateDocuments, setOpenMutateDocuments] = useState<Pick<MutateDocumentDialogBodyProps, 'action' | 'items'>>()
    const [openImportDocuments, setOpenImportDocuments] = useState<Pick<ImportDocumentDialogBodyProps, 'items'>>()
    const [openDeleteDocuments, setOpenDeleteDocuments] = useState(false)
    const [openComposeEmail, setOpenComposeEmail] = useState(false)
    const [openPreviewDocument, setOpenPreviewDocument] = useState<FolderDriveItem>()

    const dropzoneRef = useRef<DropzoneRef>()

    const [driveItems, setDriveItems] = useState<FolderDriveItem[]>([])

    const updateDriveItemsFunc = useRef<(addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) => void>(() => {})
    const reloadDriveItemsFunc = useRef<() => void>(() => {})

    const submitPdfRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.PdfConversion,
        {
            [QueueStatus.Finished]: () => {
                reloadDriveItemsFunc.current?.()
            },
        },
        true,
    )

    const buttons = useMemo<HammockToolbarButtonData[]>((): HammockToolbarButtonData[] => {
        return [
            {
                value: 'email-add-document',
                icon: <AttachIcon />,
                titleKey: 'file.pivot.document.email.add',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                onClick: () => {
                    Promise.all(
                        props.selectedDriveItems
                            .filter(item => item.itemType === 'leaf')
                            .map(item =>
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                                    Office.context.mailbox.item.addFileAttachmentAsync(downloadUrl, item.name)
                                }),
                            ),
                    ).then(() => {
                        props.setSelectedDriveItems([])
                        props.setSelectedDriveItemIds([])
                    })
                },
            },
            {
                value: 'email-add-document-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.email.add.pdf',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                onClick: () => {
                    props.selectedDriveItems
                        .filter(item => item.itemType === 'leaf')
                        .forEach(async item => {
                            if (isConvertableToPdf(item.name)) {
                                const payload = {
                                    itemId: item.id,
                                    fileName: item.name,
                                    folderId: item.parent.id,
                                }
                                submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'), {
                                    [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                                        if (Office.context.mailbox.item.addFileAttachmentAsync) {
                                            Office.context.mailbox.item.addFileAttachmentAsync(
                                                getDownloadUrl(e.status.driveItem),
                                                e.status.driveItem.name,
                                            )
                                        } else {
                                            const error = new Error(
                                                translate('file.pivot.document.error.outdated_outlook', {
                                                    host: Office.context.diagnostics.host,
                                                    version: Office.context.diagnostics.version,
                                                    platform: Office.context.diagnostics.platform,
                                                }),
                                            )
                                            appInsightsReact.trackException({
                                                exception: error,
                                                severityLevel: SeverityLevel.Error,
                                                properties: {
                                                    reactComponent: 'DocumentCommandBar',
                                                    emailId: Office.context.mailbox?.item?.itemId,
                                                },
                                            })
                                            throw error
                                        }
                                    },
                                })
                            } else {
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl =>
                                    Office.context.mailbox.item.addFileAttachmentAsync(downloadUrl, item.name),
                                )
                            }
                        })
                    props.setSelectedDriveItems([])
                    props.setSelectedDriveItemIds([])
                },
            },
            {
                value: 'add',
                icon: <AddIcon />,
                titleKey: 'file.pivot.document.create.label',
                filter: () => !isAppContext([AppContext.Compose]),
                childButtons: [
                    {
                        value: 'add-document',
                        icon: <DocumentAddIcon />,
                        titleKey: 'file.pivot.document.create.standard',
                        onClick: () => setOpenCreateStandardDocument(true),
                    },
                    {
                        value: 'add-task',
                        icon: <TaskAddIcon />,
                        titleKey: 'file.pivot.document.create.task',
                        onClick: () =>
                            history.push(
                                generatePath(`${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`, routeMatch.params),
                                {
                                    attachmentIds: props.selectedDriveItems?.map(item => item.id) ?? [],
                                },
                            ),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-email',
                        icon: <MailAddIcon />,
                        titleKey: 'file.pivot.document.create.email',
                        onClick: () => setOpenComposeEmail(true),
                        filter: () => isOfficeInitialized(),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-root-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.root',
                        onClick: () => setOpenCreateRootFolder(true),
                    },
                    {
                        value: 'add-sub-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.sub',
                        onClick: () => setOpenCreateSubFolder(true),
                    },
                ],
            },
            {
                value: 'divider1',
                divider: true,
                filter: () => !isAppContext([AppContext.Compose]),
            },
            {
                value: 'convert-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.convert.pdf',
                filter: () => !isAppContext([AppContext.Compose]),
                disabled:
                    props.selectedDriveItems.length === 0 ||
                    props.selectedDriveItems.filter(item => item.itemType === 'branch').length > 0 ||
                    props.selectedDriveItems.some(item => !isConvertableToPdf(item.name)),
                onClick: () => {
                    props.selectedDriveItems.forEach(item => {
                        if (isConvertableToPdf(item.name)) {
                            const payload = {
                                itemId: item.id,
                                fileName: item.name,
                                folderId: item.parent.id,
                            }
                            submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'))
                        }
                    })
                },
            },
            {
                value: 'rename',
                icon: <RenameIcon />,
                titleKey: 'file.pivot.document.mutate.rename.button',
                disabled: props.selectedDriveItems.length !== 1,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Rename, items: props.selectedDriveItems }),
            },
            {
                value: 'copy',
                icon: <CopyIcon />,
                titleKey: 'file.pivot.document.mutate.copy.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Copy, items: props.selectedDriveItems }),
            },
            {
                value: 'move',
                icon: <MoveIcon />,
                titleKey: 'file.pivot.document.mutate.move.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Move, items: props.selectedDriveItems }),
            },
            {
                value: 'delete',
                icon: <DeleteIcon />,
                titleKey: 'file.pivot.document.delete.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenDeleteDocuments(true),
            },
            {
                value: 'import',
                icon: <ImportIcon />,
                titleKey: 'file.pivot.document.import.button',
                onClick: () => dropzoneRef.current?.open(),
            },
            {
                value: 'divider2',
                divider: true,
            },
            {
                value: 'sort',
                icon: <SortIcon />,
                titleKey: 'sorting.label',
                childButtons: [
                    {
                        value: 'filter-a-z',
                        titleKey: 'sorting.az',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameAsc),
                    },
                    {
                        value: 'filter-z-a',
                        titleKey: 'sorting.za',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameDesc),
                    },
                    {
                        value: 'filter-old-new',
                        titleKey: 'sorting.dateasc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedAsc),
                    },
                    {
                        value: 'filter-new-old',
                        titleKey: 'sorting.datedesc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedDesc),
                    },
                ],
            },
        ]
    }, [props.selectedDriveItems])

    return (
        <>
            <Card className={pageStyles.card}>
                <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                    open={openCreateStandardDocument}
                    setOpen={setOpenCreateStandardDocument}
                    dialogFormProps={() => ({
                        initialValues: {
                            fileId: props.fileId,
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getOfficeDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={OfficeDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        context: DocumentContext.Document,
                        folders: driveItems,
                        parentId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateRootFolder}
                    setOpen={setOpenCreateRootFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: undefined,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateRootFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: true,
                        folders: driveItems,
                        parentId: props.file.documentRootId,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateSubFolder}
                    setOpen={setOpenCreateSubFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateSubFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: false,
                        folders: driveItems,
                        parentId: undefined,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockFormDialog<Partial<MutateDocumentDialogBodyValues>, MutateDocumentDialogBodyProps>
                    open={openMutateDocuments?.items?.length > 0}
                    setOpen={(open: boolean) =>
                        setOpenMutateDocuments(open ? openMutateDocuments : { action: MutateDocumentAction.Rename, items: [] })
                    }
                    dialogFormProps={() => ({
                        initialValues: {
                            name: openMutateDocuments?.items?.[0]?.name,
                            fileId: props.fileId,
                            folderId: openMutateDocuments?.items?.[0]?.parent?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getMutateDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={MutateDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: driveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                        ...openMutateDocuments,
                    })}
                />
                <HammockDialog<DeleteDocumentDialogBodyProps>
                    open={openDeleteDocuments}
                    setOpen={setOpenDeleteDocuments}
                    dialogBody={DeleteDocumentDialogBody}
                    dialogBodyProps={() => ({
                        items: props.selectedDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockDialog<PreviewDocumentDialogBodyProps>
                    open={openPreviewDocument !== undefined}
                    setOpen={(open: boolean) => setOpenPreviewDocument(open ? openPreviewDocument : undefined)}
                    dialogBody={PreviewDocumentDialogBody}
                    dialogBodyProps={() => ({
                        driveItem: openPreviewDocument,
                    })}
                />
                <HammockDialog<ComposeEmailDialogBodyProps>
                    open={openComposeEmail}
                    setOpen={setOpenComposeEmail}
                    dialogBody={ComposeEmailDialogBody}
                    dialogBodyProps={() => props}
                />
                <HammockFormDialog<Partial<ImportDocumentDialogBodyValues>, ImportDocumentDialogBodyProps>
                    open={openImportDocuments?.items?.length > 0}
                    setOpen={(open: boolean) => setOpenImportDocuments(open ? openImportDocuments : { items: [] })}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: props.selectedDriveItems[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getImportDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={ImportDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: driveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                        ...openImportDocuments,
                    })}
                />

                <OverflowToolbarPanel
                    toolbarProps={toolbarProps}
                    getToolbarButtonProps={(button?: HammockToolbarButtonData) => ({
                        appearance: 'subtle',
                        disabled: isLoading || button?.disabled,
                    })}
                    getMenuItemProps={(button?: HammockToolbarButtonData) => ({
                        disabled: isLoading || button?.disabled,
                    })}
                    buttons={buttons}
                    filter={buttons => buttons?.filter((item: HammockToolbarButtonData) => (item.filter ? item.filter() : true))}
                    overflowButtonLabelKey={'base.item.label'}
                />
            </Card>
            <Card className={pageStyles.card}>
                <Input placeholder={translate('file.pivot.document.search')} appearance={'underline'} disabled />
            </Card>
            <Card className={pageStyles.card}>
                <Dropzone
                    disabled={driveItems?.filter(item => item.itemType === 'branch').length === 0}
                    dropzoneRef={dropzoneRef}
                    onDropAccepted={(files: File[]) => {
                        setOpenImportDocuments({ items: files })
                    }}
                >
                    <DocumentFlatTree
                        size={smallMenu ? 'small' : 'medium'}
                        rootId={props.file.documentRootId}
                        sortOrder={sortOrder}
                        selectionStrategy={DocumentSelectionStrategy.Single}
                        selectedOptions={props.selectedDriveItemIds}
                        onOptionsSelect={(ids, items) => {
                            props.setSelectedDriveItemIds(ids)
                            props.setSelectedDriveItems(items)
                        }}
                        setIsLoading={setIsLoading}
                        onDriveItemsLoaded={values => setDriveItems(values)}
                        setUpdateDriveItems={setter => (updateDriveItemsFunc.current = setter)}
                        setReloadDriveItems={setter => (reloadDriveItemsFunc.current = setter)}
                        treeItemContent={(flatTree, props, flatTreeItem) => (
                            <FileDocumentTreeItem
                                key={flatTreeItem.value}
                                {...flatTreeItem.getTreeItemProps()}
                                {...props}
                                item={flatTreeItem.getTreeItemProps()}
                                flatTreeHook={flatTree}
                                itemType={flatTreeItem.itemType}
                                size={props.size}
                                onPreviewClick={driveItem => setOpenPreviewDocument(driveItem)}
                                onRenameClick={driveItem => setOpenMutateDocuments({ action: MutateDocumentAction.Rename, items: [driveItem] })}
                            />
                        )}
                    />
                </Dropzone>
            </Card>
        </>
    )
}
